import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, EffectFade } from 'swiper/modules';
import type { ImageArrayConfig, ImageArrayItem } from '@/types/page';
import { getSpacingStyle, getBorderRadiusStyle } from '@/utils/pageUtils';

// 导入 Swiper 样式
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

interface ImageArrayComponentProps {
  config: ImageArrayConfig;
}

export default function ImageArrayComponent({ config }: ImageArrayComponentProps) {
  if (!config.images || config.images.length === 0) {
    return null;
  }

  // 应用 spacing 样式
  const spacingStyle = getSpacingStyle(config.spacing);
  // 应用 borderRadius 样式
  const borderRadiusStyle = getBorderRadiusStyle(config.borderRadius);

  // 判断是否为自由比例模式
  const isFreeMode = config.aspectRatio === 'FREE';

  // 渲染单个图片项
  const renderImageItem = (item: ImageArrayItem, index: number) => {
    const imageUrl = item.imageUri;
    if (!imageUrl) return null;

    // 无论什么模式都尊重配置的 objectFit，默认值为 cover
    const objectFit = config.objectFit || 'cover';

    const imageElement = (
      <img
        src={imageUrl}
        alt={item.alt || `图片${index + 1}`}
        style={{ width: '100%', height: '100%', objectFit, ...borderRadiusStyle }}
        loading="lazy"
      />
    );

    // 如果有链接，包裹 <a> 标签
    if (item.link) {
      return (
        <a href={item.link} target="_blank" rel="noopener noreferrer">
          {imageElement}
        </a>
      );
    }

    return imageElement;
  };

  // 轮播模式
  if (config.displayMode === 'CAROUSEL') {
    // 指示器样式映射
    const paginationType = config.indicatorStyle === 'DOTS' ? 'bullets'
      : config.indicatorStyle === 'NUMBERS' ? 'fraction'
      : config.indicatorStyle === 'LINES' ? 'progressbar'
      : false;

    // 切换效果
    const effect = config.transition === 'FADE' ? 'fade' : 'slide';

    // 根据效果类型决定模块
    const modules = effect === 'fade'
      ? [Autoplay, Pagination, EffectFade]
      : [Autoplay, Pagination];

    // 计算轮播样式：自由模式使用固定高度，否则使用纵横比
    const carouselStyle = isFreeMode && config.customHeight
      ? { height: config.customHeight, width: '100%' }
      : { aspectRatio: config.aspectRatio || '16/9' };

    return (
      <div style={spacingStyle}>
        <Swiper
          modules={modules}
          effect={effect}
          spaceBetween={0}
          slidesPerView={1}
          autoplay={config.autoPlay ? { delay: config.interval || 3000 } : false}
          pagination={paginationType ? { type: paginationType } : false}
          loop={config.images.length > 1}
          style={carouselStyle}
        >
          {config.images.map((item, index) => (
            <SwiperSlide key={index}>
              {renderImageItem(item, index)}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  }

  // 网格模式
  const columns = config.columns || 2;
  const gap = config.gap || 16;

  // 计算网格项样式：自由模式使用固定高度，否则使用纵横比
  const itemStyle = isFreeMode && config.customHeight
    ? { height: config.customHeight, width: '100%', overflow: 'hidden' }
    : { aspectRatio: config.aspectRatio || '16/9', overflow: 'hidden' };

  return (
    <div style={spacingStyle}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: `${gap}px`,
        }}
      >
        {config.images.map((item, index) => (
          <div
            key={index}
            style={itemStyle}
          >
            {renderImageItem(item, index)}
          </div>
        ))}
      </div>
    </div>
  );
}
